import React from 'react';
import { Link } from 'react-router-dom';
import { Eye, Target, Scale, Heart, ArrowRight } from 'lucide-react';
import SectionTitle from '../components/ui/SectionTitle';
import Button from '../components/ui/Button';
import InquiryForm from '../components/home/InquiryForm';
import WhatsAppFloatingButton from '../components/layout/WhatsAppFloatingButton';

// Import local image
import thefirmImg from '../assets/thefirm.png';

const TheFirm = () => {
  const pillars = [
    {
      icon: Scale,
      title: "Strategic Advocacy",
      description: "We align every legal move with your long-term commercial or personal objectives."
    },
    {
      icon: Target,
      title: "Problem-Solving",
      description: "We don't just identify risks; we engineer innovative solutions to resolve them swiftly."
    },
    {
      icon: Heart,
      title: "Client Centricity",
      description: "We operate on trust and reliability, ensuring you are never treated as just another file."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-primary-blue min-h-[35vh] flex items-center">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-60"
          style={{ backgroundImage: `url(${thefirmImg})` }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center text-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">The Firm</h1>
              <p className="text-xl text-gray-300">Where Justice Meets Compassion</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-8">
{/* About Section */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-3xl font-bold text-primary-blue mb-6">About AKY Legal Consultants</h2>
              <p className="text-dark-gray text-lg mb-4 leading-relaxed">
                <span className="text-gold font-semibold">Where Justice Meets Compassion</span>
              </p>
              <p className="text-dark-gray text-lg mb-6 leading-relaxed">
                AKY Legal Consultants has been a beacon of trusted legal counsel in Mumbai, Maharashtra. Operating under the guiding principle of "Where Justice Meets Compassion," we are dedicated to delivering excellence while ensuring our clients feel supported every step of the way.
              </p>
              <p className="text-dark-gray text-lg mb-6 leading-relaxed">
                Our firm provides a comprehensive range of legal services, with particular expertise in Civil Litigation, Criminal Litigation, Real Estate, and Family Law. We are admitted to practice before the Bombay High Court and various other judicial forums across the city.
              </p>
              
              <h3 className="text-2xl font-bold text-primary-blue mb-4">Led by Expertise</h3>
              <p className="text-dark-gray text-lg mb-6 leading-relaxed">
                Founder <span className="font-semibold text-primary-blue">Adv. Pintu Arjun Yadav</span> (M.COM, BBI, LL.B) brings a unique perspective to the firm. By combining rigorous financial literacy with deep legal expertise, he ensures that every strategy is not only legally sound but also financially viable for the client.
              </p>
              
              <h3 className="text-2xl font-bold text-primary-blue mb-4">Our Commitment to You</h3>
              <p className="text-dark-gray text-lg mb-6 leading-relaxed">
                At AKY Legal Consultants, you are more than just a case number. We believe in offering personalized service, guiding individuals, business owners, and organizations through complex legal matters with clarity and empathy.
              </p>
              <p className="text-dark-gray text-lg leading-relaxed">
                Get in touch with us today to experience legal assistance that is reliable, friendly, and effective.
              </p>
            </div>

            {/* Vision & Mission */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <Eye className="w-12 h-12 text-gold mb-4" />
                <h3 className="text-2xl font-bold text-primary-blue mb-4">Our Vision</h3>
                <p className="text-dark-gray">
                  To be the premier multi-disciplinary legal partner in Maharashtra. We are aggressively 
                  expanding our footprint into emerging sectors—including Fintech, Cyber Law, and Data 
                  Protection—to ensure our clients stay protected in an evolving digital economy.
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-xl p-8">
                <Target className="w-12 h-12 text-gold mb-4" />
                <h3 className="text-2xl font-bold text-primary-blue mb-4">Our Mission</h3>
                <p className="text-dark-gray">
                  We dismantle the complexity of the legal system to provide clear, efficient, and actionable outcomes. 
                  Our focus is on delivering elite advocacy that remains accessible and transparent in pricing.
                </p>
              </div>
            </div>

            {/* Core Pillars */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-3xl font-bold text-primary-blue mb-8 text-center">Core Pillars of Practice</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {pillars.map((pillar, index) => {
                  const Icon = pillar.icon;
                  return (
                    <div key={index} className="text-center">
                      <div className="w-16 h-16 bg-primary-blue rounded-xl flex items-center justify-center mx-auto mb-4">
                        <Icon className="w-8 h-8 text-gold" />
                      </div>
                      <h3 className="text-lg font-bold text-primary-blue mb-2">{pillar.title}</h3>
                      <p className="text-dark-gray text-sm">{pillar.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right Column - Inquiry Form */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl p-6 sticky top-24">
              <h3 className="text-2xl font-bold text-primary-blue mb-6 text-center">Free Consultation</h3>
              <InquiryForm />
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp Floating Button */}
      <WhatsAppFloatingButton />   
    </div>
  );
};

export default TheFirm;